import { userState } from "@mirage/store/UserStore";
import { ErrorSharp, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack } from "@mui/material";
import { AppLoginRequest, LoginResult } from "@shared/mirage/core/contracts/User";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { ILoginService } from "@shared/mirage/services/ILoginService";
import validators, { ValidationResult } from "@shared/mirage/validators";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";

interface LoginBoilerplateProps {
    navigateTo: string;
    service: ILoginService;
    Buttons: React.FC<{ handleSignIn: () => Promise<void> }>;
    fieldSize?: "small" | "medium";
    appName: string;
    customErrorHandler?: (error: string, email: string, serverValidationHandler: (error: string, passed: boolean) => void) => void;
}

const LoginBoilerplate = ({ fieldSize = "small", Buttons, ...props }: LoginBoilerplateProps) => {
    const [showPassword, setShowPassword] = useState(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [emailValidation, setEmailValidation] = useState({ error: "", passed: true });
    const [passwordValidation, setPasswordValidation] = useState({ error: "", passed: true });
    const navigate = useNavigate();
    const setUserState = useSetRecoilState(userState);

    const emailValidationHandler = (value: string) => {
        setEmailValidation(() => {
            const validationResult = validators.EmailValidator(value);
            return {
                error: validationResult.error || "",
                passed: validationResult.passed,
            };
        });
    };

    const passwordValidationHandler = (value: string) => {
        setPasswordValidation(() => {
            const validationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter a password");
            return {
                error: validationResult?.error ? validationResult?.error : "",
                passed: validationResult?.passed ? validationResult?.passed : false,
            };
        });
    };

    const handleInputOnChange = (reference: "email" | "password", value: string) => {
        switch (reference) {
            case "email":
                setEmailAddress(() => value);
                setEmailValidation({ error: "", passed: true });
                break;
            case "password":
                setPassword(() => value);
                setPasswordValidation({ error: "", passed: true });
                break;
            default:
                throw Error(`Bad reference for validation - ${reference}`);
        }
    };

    const serverValidationHandler = (message: string, passed: boolean) => {
        setPasswordValidation(() => {
            return { error: message, passed: passed };
        });

        setEmailValidation(() => {
            return { error: message, passed: passed };
        });
    };

    const handleSignIn = async () => {
        const loginRequest: AppLoginRequest = {
            email: emailAddress,
            password,
            app: props.appName,
            timeZone: DateFormatter.currentTimezone,
        };
        if (emailAddress && password) {
            const loginResult: LoginResult = await props.service.login(loginRequest);
            if (loginResult.userId) {
                setUserState({ id: loginResult.userId, permissions: loginResult.permissions, subModules: loginResult.subModules, superSetUrl: loginResult.superSetUrl });
                navigate(props.navigateTo, { replace: true });
            } else if (loginResult.errorMsg) {
                if (typeof props.customErrorHandler === "function") {
                    props.customErrorHandler(loginResult.errorMsg, emailAddress, serverValidationHandler);
                } else {
                    serverValidationHandler(loginResult.errorMsg, false);
                }
            }
        } else {
            emailValidationHandler(emailAddress);
            passwordValidationHandler(password);
        }
    };

    const getPassFieldsEndAdornment = () => {
        return (
            <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} onMouseDown={handleMouseDownPassword}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
        );
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const getFields = () => {
        return (
            <>
                <FormControl size={fieldSize} variant="outlined" sx={{ width: "60%" }} key={`login-email-key`} required={true}>
                    <InputLabel error={!emailValidation.passed} htmlFor={"login-password-id"}>
                        Email Address
                    </InputLabel>
                    <OutlinedInput
                        defaultValue={emailAddress}
                        id={"login-email-id"}
                        type={"email"}
                        label={"Email Address"}
                        error={!emailValidation.passed}
                        onChange={(event) => handleInputOnChange("email", event.target.value)}
                    />
                    <FormHelperText error={!emailValidation.passed} sx={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                        {!emailValidation.passed && <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }} />}
                        {!emailValidation.passed && emailValidation.error}
                    </FormHelperText>
                </FormControl>
                <FormControl variant="outlined" size={fieldSize} sx={{ width: "60%" }} key={`login-password-key`} required={true}>
                    <InputLabel error={!passwordValidation.passed} htmlFor={"login-password-id"}>
                        Password
                    </InputLabel>
                    <OutlinedInput
                        defaultValue={password}
                        id={"login-password-id"}
                        type={showPassword ? "text" : "password"}
                        label={"Password"}
                        error={!passwordValidation.passed}
                        onChange={(event) => handleInputOnChange("password", event?.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && handleSignIn()}
                        endAdornment={getPassFieldsEndAdornment()}
                    />
                    <FormHelperText error={!passwordValidation.passed} sx={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                        {!passwordValidation.passed && <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }} />}
                        {!passwordValidation.passed && passwordValidation.error}
                    </FormHelperText>
                </FormControl>
            </>
        );
    };

    return (
        <div style={{ padding: "1rem 0 0 0" }}>
            <Box>
                <Stack spacing={4} justifyContent={"center"} alignItems={"center"}>
                    {getFields()}
                    <Buttons handleSignIn={handleSignIn} />
                </Stack>
            </Box>
        </div>
    );
};

export default LoginBoilerplate;
